<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/visibility_photos/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.visibilityPhotos')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <ExportLink
        url="/visibility_photos/download-zip"
        :filter="filter"
        :label="$t('toolbar.export.zipDownload')"
        :pre-export-check="handleDownloadLimit"
        :class="{ 'disabled-link mt-5': isExportDisabled, 'mt-5': !isExportDisabled }"
      />
      <photos-download-warning-popup
        :open="openWarningPopup"
        :photo-count="total"
        :is-verifications="false"
        @confirm="handleConfirm"
        @cancel="handleClose"
      />
    </export>
  </toolbar>
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.visibility.photo.tableHeader') }}
      </div>
      <visibility-photo-modal
        v-model:open="openEditRatingModal"
        v-model:photo="editPhoto"
        @close="refetch"
      />
      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :row-click="openModal"
          :visible-columns="visibleColumns"
          :actions="actions"
        >
          <template #file="{ item }">
            <file
              v-slot="{ url, type }"
              :src="item.file && thumbnailUrl(item.file, { width: 500, height: 500 })"
              class="py-4 h-full"
            >
              <box :aspect="1" class="medium m-auto">
                <medium
                  v-if="url"
                  class="w-auto h-full"
                  :url="url"
                  :type="type"
                />
              </box>
            </file>
          </template>
        </data-table>
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, type ProviderParams, useProvider } from '@utils/table/hooks/provider';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import { format } from 'date-fns';
import File from '@utils/File.vue';
import Box from '@utils/Box.vue';
import Medium from '@utils/Medium.vue';
import VisibilityPhotoModal from '@pages/visibility/visibilityPhoto/VisibilityPhotoModal.vue';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import { useNotify } from '@utils/notifications';
import PhotosDownloadWarningPopup from '@pages/images/photos/PhotosDownloadWarningPopup.vue';
import type {
  Campaign,
  HydraCollection,
  Region,
  VisibilityPhoto,
} from '@/entities/Entities';
import { thumbnailUrl } from '@/utils/api';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);

const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteVisibilityPhoto, { loading: deleting }] = useDelete(
  t('pages.visibility.photo.actions.deleteWarning'),
);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.visibility.photo.actions.delete'),
    action: async (item: VisibilityPhoto) => {
      if (await deleteVisibilityPhoto(`/visibility_photos/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const openEditRatingModal = ref(false);
const editPhoto = ref<VisibilityPhoto>();

const openModal = (item: VisibilityPhoto): void => {
  openEditRatingModal.value = true;
  editPhoto.value = { ...item };
};

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.visibility.photo.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.visibility.photo.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'visibilityTransfer.visibilityPlanning.campaign.name',
    title: t('visibility.planning.campaign'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.photo.fieldGroups.general'),
    to: (photo: VisibilityPhoto) => ({
      name: 'campaign-group-details',
      params: {
        campaignGroupId: photo?.visibilityTransfer?.visibilityPlanning?.campaign?.group?.id,
      },
      query: {
        globalFilter: photo?.visibilityTransfer?.visibilityPlanning?.region?.id
          ? encodeURIComponent(
            JSON.stringify({
              regions: [photo?.visibilityTransfer?.visibilityPlanning?.region?.id],
            }),
          )
          : undefined,
      },
    }),
    filter: {
      key: 'visibilityTransfer.visibilityPlanning.campaign',
      type: 'dropdown',
      group: t('pages.visibility.photo.fieldGroups.general'),
      options: {
        label: t('visibility.campaign'),
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 400,
        createOption: true,
        resolveOnLoad: true,
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { 'group.name': input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
      },
    },
  },
  {
    key: 'visibilityTransfer.visibilityPlanning.region.name',
    title: t('region'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'visibilityTransfer.visibilityPlanning.region',
      type: 'dropdown',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>(
          '/regions',
          {
            sort: {
              sortKey: 'name',
              sortDirection: 'ASC',
            },
          },
          (value) => ({
            label: value.name,
            value: value.id,
          }),
        ),
        label: t('region'),
      },
    },
  },
  {
    key: 'visibilityTransfer.visibilityBicycle.number',
    title: t('visibility.bicycle'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.photo.fieldGroups.general'),
    to: (photo: VisibilityPhoto) => ({
      name: 'bicycles',
      query: {
        filter: encodeURIComponent(
          JSON.stringify({
            id: photo?.visibilityTransfer?.visibilityBicycle?.id,
          }),
        ),
      },
    }),
    filter: {
      type: 'range',
      group: t('pages.visibility.photo.filterGroups.general'),
      options: {
        label: t('visibility.bicycle'),
      },
    },
  },
  {
    key: 'owner.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.photo.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.photo.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'owner.lastName',
    title: t('user.lastName'),
    group: t('pages.visibility.photo.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.visibility.photo.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'file',
    title: t('photo'),
    group: t('pages.visibility.photo.fieldGroups.general'),
    sortable: false,
    visible: true,
  },
  {
    key: 'gisLocation',
    title: t('pages.images.photos.ratingModal.coordinates'),
    group: t('pages.visibility.photo.fieldGroups.general'),
    formatter: (v: { lat: number; long: number } | null) => (v ? `${v.lat}, ${v.long}` : ''),
    sortable: false,
  },
  {
    key: 'creationTimestamp',
    title: t('uploadedAt'),
    group: t('pages.visibility.photo.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v && format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    visible: true,
    filter: {
      type: 'datetime',
      group: t('pages.visibility.photo.filterGroups.general'),
      options: {
        label: t('uploadedAt'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.visibility.photo.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.photo.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<VisibilityPhoto>>('/visibility_photos', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);

const openWarningPopup = ref(false);
const notify = useNotify();
let resolveDownloadPromise: () => void;
let rejectDownloadPromise: () => void;
const maxDownloadLimit = computed(
  () => window.env.VITE_MAX_DOWNLOAD_LIMIT || import.meta.env.VITE_MAX_DOWNLOAD_LIMIT,
);
const warningDownloadLimit = computed(
  () => window.env.VITE_WARNING_DOWNLOAD_LIMIT || import.meta.env.VITE_WARNING_DOWNLOAD_LIMIT,
);
const isExportDisabled = computed(() => total.value && total.value > maxDownloadLimit.value);

const handleDownloadLimit = async (): Promise<boolean> => {
  // if the total number of photos is greater than the warning limit but lower than the download limit, show a warning popup
  if (total.value && total.value > warningDownloadLimit.value && !isExportDisabled.value) {
    openWarningPopup.value = true;

    return new Promise((resolve) => {
      resolveDownloadPromise = () => {
        openWarningPopup.value = false;
        resolve(true);
      };
      rejectDownloadPromise = () => {
        openWarningPopup.value = false;
        resolve(false);
      };
    });
    // if the total number of photos is greater than the download limit, show an error notification
  }
  if (isExportDisabled.value) {
    notify?.({
      variant: 'error',
      title: t('toolbar.export.downloadLimitError', { limit: maxDownloadLimit.value }),
      duration: 4000,
    });
    return false;
  }
  return true;
};

const handleClose = () => {
  openWarningPopup.value = false;
  rejectDownloadPromise();
};

const handleConfirm = () => {
  resolveDownloadPromise();
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

